import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { NativeAndroidHaptics } from "./plugins";

/** Gap between the two Light impacts when entering reorder mode. */
export const REORDER_ENTER_HAPTIC_GAP_MS = 50;

/**
 * Backward-compatible fallback for Android shells that predate NativeAndroidHaptics.
 * The updated Android shell owns the calibrated light-impact duration natively.
 */
export const ANDROID_TAP_HAPTIC_MS = 40;

export function shouldTriggerTabSwitchHaptic(destinationTab: string, activeTab: string): boolean {
  return destinationTab !== activeTab;
}

export function shouldTriggerSelectionChangeHaptic<T>(next: T, current: T): boolean {
  return next !== current;
}

async function fireNativeTapHaptic(): Promise<void> {
  if (Capacitor.getPlatform() === "android") {
    if (Capacitor.isPluginAvailable("NativeAndroidHaptics")) {
      await NativeAndroidHaptics.lightImpact();
      return;
    }

    await Haptics.vibrate({ duration: ANDROID_TAP_HAPTIC_MS });
    return;
  }

  await Haptics.impact({ style: ImpactStyle.Light });
}

function runNativeLightImpact(): void {
  if (!Capacitor.isNativePlatform()) return;

  void fireNativeTapHaptic().catch(() => {
    // Native plugin unavailable — ignore silently.
  });
}

export function triggerLightTabHaptic(): void {
  runNativeLightImpact();
}

/** Same Light impact as tab switches — onboarding forward navigation. */
export function triggerOnboardingAdvanceHaptic(): void {
  triggerLightTabHaptic();
}

export function triggerSelectionChangeHaptic(): void {
  runNativeLightImpact();
}

export function triggerTabSwitchHapticIfNeeded(destinationTab: string, activeTab: string): void {
  if (!shouldTriggerTabSwitchHaptic(destinationTab, activeTab)) return;
  triggerLightTabHaptic();
}

export function triggerReorderEnterHaptic(): void {
  if (!Capacitor.isNativePlatform()) return;

  void (async () => {
    try {
      await fireNativeTapHaptic();
      await new Promise((resolve) => setTimeout(resolve, REORDER_ENTER_HAPTIC_GAP_MS));
      await fireNativeTapHaptic();
    } catch {
      // Native plugin unavailable — ignore silently.
    }
  })();
}

/** Single Light impact during swipe-to-unlock drag (progressive or threshold confirm). */
export function triggerSwipeProgressHaptic(): void {
  runNativeLightImpact();
}

/** Rapid Light impacts for hold-to-unlock countdown seconds (3/2/1 pulses). */
export function triggerHoldCountdownHaptic(pulseCount: number): void {
  if (!Capacitor.isNativePlatform() || pulseCount < 1) return;

  void (async () => {
    try {
      for (let i = 0; i < pulseCount; i += 1) {
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, REORDER_ENTER_HAPTIC_GAP_MS));
        }
        await fireNativeTapHaptic();
      }
    } catch {
      // Native plugin unavailable — ignore silently.
    }
  })();
}
